import { api, unwrap } from '$lib/api/client';
import type { FavoriteCadence, Recipe } from '$lib/api/types';
import { session } from '$lib/auth/session.svelte';

/** The recipe collection, shared by Madplan (picker, meal cards) and Retter. */
class RecipesState {
	items = $state<Recipe[]>([]);
	loaded = $state(false);
	/** The recipes listed in Retter and offered for planning: not copies from Inspiration the household didn't keep. */
	collection = $derived(this.items.filter((recipe) => recipe.inCollection));
	private byId = $derived(
		new Map(this.items.map((recipe, index) => [recipe.id, { recipe, index }]))
	);

	async refresh() {
		this.items = await unwrap(api.GET('/api/recipes'));
		this.loaded = true;
	}

	get(id: string): Recipe | undefined {
		return this.byId.get(id)?.recipe;
	}

	/**
	 * Marks or unmarks the recipe as the signed-in user's favourite ("Min livret"). The heart changes at once and
	 * changes back when saving fails; the error is rethrown for the caller to show.
	 */
	async setFavorite(id: string, favorite: boolean) {
		const before = this.get(id);
		const userId = session.user?.id;
		if (before && userId) {
			const others = before.livret.filter((l) => l.userId !== userId);
			this.patch(id, {
				isFavorite: favorite,
				livret: favorite ? [...others, { userId, cadence: 'default', cadenceSetAt: null }] : others
			});
		}
		const path = { params: { path: { recipeId: id } } };
		try {
			this.patch(
				id,
				await unwrap(
					favorite
						? api.PUT('/api/recipes/{recipeId}/favorite', path)
						: api.DELETE('/api/recipes/{recipeId}/favorite', path)
				)
			);
		} catch (error) {
			if (before) this.patch(id, { isFavorite: before.isFavorite, livret: before.livret });
			throw error;
		}
	}

	/** Says when the signed-in user wants their favourite again, after they made it. */
	async setCadence(id: string, cadence: FavoriteCadence) {
		this.patch(
			id,
			await unwrap(
				api.PUT('/api/recipes/{recipeId}/favorite/cadence', {
					params: { path: { recipeId: id } },
					body: { cadence }
				})
			)
		);
	}

	private patch(id: string, changes: Partial<Recipe>) {
		this.items = this.items.map((recipe) =>
			recipe.id === id ? { ...recipe, ...changes } : recipe
		);
	}

	/** Position in the collection; decides the recipe's tile colour. */
	indexOf(id: string | null | undefined): number {
		return (id && this.byId.get(id)?.index) || 0;
	}
}

export const recipes = new RecipesState();
